import React, { useRef, useState } from "react";
import { HomeOutlined, UserOutlined, SearchOutlined } from "@ant-design/icons";
import { Breadcrumb, Form, Input, Button, Space, Table, notification, Switch } from "antd";
import type { InputRef, TableColumnsType, TableColumnType } from 'antd';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import useSWR from 'swr';
import { getDisplayStatus, statusMapRecord } from "../../components/hoc_picking_list";
import { useAuth } from "../../context/AuthContext";
import "./browser_picking_list.css";


interface PickingListRecord {
    id: number;
    key: React.Key;
    status: string;
    no_picking: string;
    date_picking: string;
    sales_order: string;
    customer_name: string;
    remark: string;
    purchase_order?: string;
    warehouse_group?: string;
}

type DataIndex = keyof PickingListRecord;
const backendUrl = process.env.REACT_APP_BACKEND_URL;
const fetcher = (url: string) => fetch(url).then((res) => {
    if (!res.ok) {
        throw new Error('Failed to fetch data');
    }
    return res.json();
});

const warehouseLabelMap: Record<string, string> = {
    "WH_AMP_SPT": "Warehouse AMP-SAMPIT",
    "WH_AMP_TWH": "Warehouse AMP-TEWEH",
    "WH_AMP_PKY": "Warehouse AMP-PALANGKARAYA",
    "WH_AMP_PBUN": "Warehouse AMP-PANGKALANBUN",
};

const getWarehouseLabel = (warehouse: string | undefined): string => {
    if (!warehouse) return "-";
    return warehouseLabelMap[warehouse] || warehouse;
};
const BrowserPickingList: React.FC = () => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };

    const { userData } = useAuth();
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef<InputRef>(null);
    const [showAllWarehouse, setShowAllWarehouse] = useState(false);

    const { data: pickingLists, error: pickingListsError, isLoading: pickingListsLoading } = useSWR<PickingListRecord[]>(
        `${backendUrl}/api/picking-lists`,
        fetcher
    );

    if (pickingListsError) {
        console.error("Gagal memuat daftar picking list:", pickingListsError);
        notification.error({ message: "Gagal memuat daftar picking list dari server." });
    }

    const handleSearch = (
        selectedKeys: string[],
        confirm: FilterDropdownProps['confirm'],
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<PickingListRecord> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                <Input
                    ref={searchInput}
                    placeholder={`Cari ${dataIndex === 'no_picking' ? 'Nomor Picking List' : dataIndex === 'date_picking' ? 'Tanggal Picking List' : dataIndex === 'sales_order' ? 'Nomor Sales Order' : dataIndex === 'customer_name' ? 'Nama Pelanggan' : dataIndex === 'purchase_order' ? 'Purchase Order No' : dataIndex === 'remark' ? 'Remark' : ''}`}
                    value={selectedKeys[0]}
                    onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                    onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                    style={{ marginBottom: 8, display: 'block' }}
                />
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Cari
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        Tutup
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
        ),
        onFilter: (value, record) => {
            const recordValue = record[dataIndex];
            return recordValue
                ? recordValue.toString().toLowerCase().includes((value as string).toLowerCase())
                : false;
        },
        filterDropdownProps: {
            onOpenChange(open) {
                if (open) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const handleShowDetails = (record: PickingListRecord) => {
        console.log("browser_picking_list.tsx: Menyimpan no_picking ke sessionStorage:", record.no_picking);
        sessionStorage.setItem('pickingListToEdit', record.no_picking);
        window.location.hash = '#/dashboard/picking_list';
    };

    const uniqueWarehouseFilters = Array.from(
        new Set((pickingLists || []).map((item) => item.warehouse_group).filter(Boolean))
    ).map((wh) => ({ text: getWarehouseLabel(wh), value: wh! }));

    const statusFilters = Object.entries(statusMapRecord).map(([key, label]) => ({
        text: label,
        value: key,
    }));

    const columns: TableColumnsType<PickingListRecord> = [
        {
            title: 'Picking List No',
            dataIndex: 'no_picking',
            key: 'no_picking',
            ...getColumnSearchProps('no_picking'),
            sorter: (a, b) => a.no_picking.localeCompare(b.no_picking),
            onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
        },
        {
            title: 'Picking List Date',
            dataIndex: 'date_picking',
            key: 'date_picking',
            ...getColumnSearchProps('date_picking'),
            sorter: (a, b) => new Date(a.date_picking).getTime() - new Date(b.date_picking).getTime(),
            onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
        },
        {
            title: 'Sales Order No',
            dataIndex: 'sales_order',
            key: 'sales_order',
            ...getColumnSearchProps('sales_order'),
            sorter: (a, b) => a.sales_order.localeCompare(b.sales_order),
            onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
        },
        {
            title: 'Customer Name',
            dataIndex: 'customer_name',
            key: 'customer_name',
            ...getColumnSearchProps('customer_name'),
            sorter: (a, b) => a.customer_name.localeCompare(b.customer_name),
            onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
        },
        {
            title: 'Purchase Order No',
            dataIndex: 'purchase_order',
            key: 'purchase_order',
            ...getColumnSearchProps('purchase_order'),
            sorter: (a, b) => (a.purchase_order || '').localeCompare(b.purchase_order || ''),
            onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
        },
        {
            title: 'Remark Closed Return',
            dataIndex: 'remark',
            key: 'remark',
            ...getColumnSearchProps('remark'),
            sorter: (a, b) => (a.remark || '').localeCompare(b.remark || ''),
            onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
        },
        {
            title: 'Warehouse',
            dataIndex: 'warehouse_group',
            key: 'warehouse_group',
            filters: uniqueWarehouseFilters,
            onFilter: (value, record) => record.warehouse_group === value,
            sorter: (a, b) => (a.warehouse_group || '').localeCompare(b.warehouse_group || ''),
            onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            render: (text: string) => getWarehouseLabel(text),
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            filters: statusFilters,
            onFilter: (value, record) => record.status === value,
            sorter: (a, b) => a.status.localeCompare(b.status),
            onCell: () => ({ style: { whiteSpace: 'nowrap' } }),
            render: (text: string) => {
                const displayStatus = getDisplayStatus(text);
                return searchedColumn === 'status' ? (
                    <Highlighter
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                        searchWords={[searchText]}
                        autoEscape
                        textToHighlight={displayStatus ? displayStatus.toString() : ''}
                    />
                ) : (
                    displayStatus
                );
            },

        },
    ];


    return (

        <div id="izai" style={transformStyle}>
            <div className="gjs-grid-row" id="i6xw">
                <div className="gjs-grid-column" id="iak3">
                    <Breadcrumb
                        items={[
                            {
                                title: <HomeOutlined />,
                            },
                            {
                                href: "#/dashboard/browser_picking_list",
                                title: (
                                    <>
                                        <span style={{ fontWeight: '200', color: 'rgba(0, 0, 0, 0.88)' }}>Data Picking List</span>
                                    </>
                                ),
                            },
                            {
                                href: "#/dashboard/picking_list",
                                title: "Picking List",
                            },
                        ]}
                    />
                </div>
            </div>
            <div className="gjs-grid-row" id="ig8l3">
                <div className="gjs-grid-column" id="ip2wc">
                    {userData?.otoritas?.split(';').includes('77') && (
                        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Switch checked={showAllWarehouse} onChange={setShowAllWarehouse} />
                            <span style={{ fontSize: '14px' }}>Show All Warehouse</span>
                        </div>
                    )}
                    <Table
                        columns={columns}
                        dataSource={pickingLists?.filter(item => (showAllWarehouse && userData?.otoritas?.split(';').includes('77')) || item.warehouse_group === userData?.warehouse_code).map(item => ({ ...item, key: item.id })) || []}
                        loading={pickingListsLoading}
                        pagination={{ pageSize: 10 }}
                        scroll={{ x: 'max-content' }}
                        onRow={(record) => ({
                            onDoubleClick: () => handleShowDetails(record),
                        })}
                    />
                </div>
            </div>
        </div>
    );
};

export default BrowserPickingList;